import { RefObject, useEffect } from 'react';

/**
 * Distributes leftover horizontal width equally across trailing spacer cells
 * in a table. CSS table layout doesn't reliably split `width: 100%` across
 * multiple columns (Chrome dumps the full surplus into the first cell that
 * declares the percentage), so this hook measures the real cells' natural
 * widths, computes the leftover against the container width, and publishes
 * the per-spacer width via the `--spacer-w` custom property which every
 * spacer cell reads in its inline `style`. A `ResizeObserver` keeps the
 * value in sync as the container changes; the effect also re-runs when
 * `dependencies` change so column-content shifts retrigger it.
 *
 * @param containerRef - Ref to the container element wrapping the table
 * @param tableRef - Ref to the table element
 * @param dependencies - Array of dependencies that should trigger recalculation
 */
export const useTableSpacerWidth = (
  containerRef: RefObject<HTMLDivElement | null>,
  tableRef: RefObject<HTMLTableElement | null>,
  dependencies: unknown[],
) => {
  useEffect(() => {
    const tableEl = tableRef.current;
    const containerEl = containerRef.current;
    if (!tableEl || !containerEl) return;

    const update = () => {
      const realCells = tableEl.querySelectorAll<HTMLElement>('thead th:not([data-spacer])');
      if (realCells.length === 0) {
        tableEl.style.removeProperty('--spacer-w');
        return;
      }
      const containerWidth = containerEl.clientWidth;
      const realWidth = Array.from(realCells).reduce((sum, el) => sum + el.offsetWidth, 0);
      const leftover = Math.max(0, containerWidth - realWidth);
      tableEl.style.setProperty('--spacer-w', `${leftover / realCells.length}px`);
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(containerEl);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};
